import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { WindowSizeService } from 'src/app/shared/window-size/window-size.service';
import { LoggedInService } from 'src/app/services/auth/loggedin.service';
import { AppConfigService } from 'src/environments/app-config.service';
import {
  CarSidebarChildItem,
  CarSidebarFooterItem,
  CarSidebarGroupItem,
  CarSidebarNav,
} from './car-sidebar.model';
import { MenuService } from './service/menu.service';
import {
  SUPPORT_FAQ_SECTIONS,
  SupportFaqItem,
  SupportFaqSection,
} from './support-faq.content';

@Component({
  standalone: false,
  selector: 'app-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss'],
})
export class MenuComponent implements OnInit, AfterViewInit, OnDestroy {
  private subscription = new Subscription();

  sidebarNav: CarSidebarNav | null = null;
  footerItems: CarSidebarFooterItem[] = [];
  collapsed = true;
  /** Após o fade do texto, troca para variant icon / labels vazios. */
  labelsCollapsed = true;
  supportDrawerOpen = false;
  large = false;
  activeRoute = '';
  readonly supportSections = SUPPORT_FAQ_SECTIONS;
  private readonly expandedFaqIds = new Set<string>();
  private readonly labelTransitionMs = 400;
  private labelTransitionTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private readonly menuService: MenuService,
    private readonly windowSizeService: WindowSizeService,
    private readonly loggedInService: LoggedInService,
    private readonly router: Router,
    private readonly appConfigService: AppConfigService,
  ) {}

  get supportUrl(): string {
    return this.appConfigService.config?.urlSuporteB3
      ?? 'https://atendimento.b3.com.br/atendeb3';
  }

  ngOnInit(): void {
    this.validateWindowSize();
    this.trackActiveRoute();

    this.subscription.add(
      this.menuService.sidebarNav$.subscribe(nav => {
        this.sidebarNav = nav;
        this.syncExpandedGroups();
        queueMicrotask(() => this.syncLayoutClasses());
      }),
    );

    this.subscription.add(
      this.menuService.sidebarFooter$.subscribe(items => {
        this.footerItems = items;
      }),
    );
  }

  ngAfterViewInit(): void {
    this.syncLayoutClasses();
  }

  ngOnDestroy(): void {
    this.clearLabelTransitionTimer();
    this.subscription.unsubscribe();
  }

  get isVisible(): boolean {
    return !!this.sidebarNav && !this.large;
  }

  get menuItemVariant(): 'default' | 'icon' {
    return this.labelsCollapsed ? 'icon' : 'default';
  }

  get collapseIconName(): string {
    return this.collapsed ? 'left_panel_open' : 'left_panel_close';
  }

  itemLabel(label: string): string {
    return this.labelsCollapsed ? '' : label;
  }

  toggleCollapsed(): void {
    this.clearLabelTransitionTimer();

    if (this.collapsed) {
      // Expandir: labels voltam junto com a largura.
      this.labelsCollapsed = false;
      this.collapsed = false;
      this.syncLayoutClasses();
      queueMicrotask(() => this.setMenuLabelOpacity(1));
      return;
    }

    // Colapsar: mantém o texto para fazer fade e só depois remove os labels.
    this.setMenuLabelOpacity(0);
    this.collapsed = true;
    this.syncLayoutClasses();
    this.labelTransitionTimer = setTimeout(() => {
      this.labelsCollapsed = true;
      this.labelTransitionTimer = undefined;
    }, this.labelTransitionMs);
  }

  private clearLabelTransitionTimer(): void {
    if (this.labelTransitionTimer != null) {
      clearTimeout(this.labelTransitionTimer);
      this.labelTransitionTimer = undefined;
    }
  }

  /** Fade dos textos internos do DS (shadow aberto) durante o colapso. */
  private setMenuLabelOpacity(opacity: number): void {
    const sidebar = document.getElementById('car-sidebar');
    if (!sidebar) {
      return;
    }

    sidebar.querySelectorAll('b3-menu-vertical-item').forEach(item => {
      const root = (item as HTMLElement & { shadowRoot?: ShadowRoot | null }).shadowRoot;
      if (!root) {
        return;
      }

      root.querySelectorAll('[class*="label"], [class*="Label"], [part="label"]').forEach(node => {
        if (!(node instanceof HTMLElement)) {
          return;
        }
        if (node.closest('b3-icon') || node.tagName.toLowerCase() === 'b3-icon') {
          return;
        }
        node.style.transition = 'opacity 0.35s ease';
        node.style.opacity = String(opacity);
        if (opacity === 1) {
          node.style.removeProperty('pointer-events');
        }
      });
    });
  }

  onGroupItemClick(group: CarSidebarGroupItem, event: Event): void {
    if (!this.collapsed) {
      return;
    }

    this.clearLabelTransitionTimer();
    this.labelsCollapsed = false;
    this.collapsed = false;
    group.expanded = true;
    this.syncLayoutClasses();
    queueMicrotask(() => this.setMenuLabelOpacity(1));
    event.preventDefault();
  }

  buildHref(link?: string): string | undefined {
    if (!link) {
      return undefined;
    }

    return `/${link.replace(/^\//, '')}`;
  }

  navigateTo(link: string | undefined, event: Event): void {
    event.preventDefault();

    if (!link) {
      return;
    }

    this.router.navigateByUrl(this.buildHref(link)!);
  }

  isGroupExpanded(group: CarSidebarGroupItem): boolean {
    return group.expanded || this.isGroupActive(group);
  }

  onExternalClick(event: Event): void {
    const customEvent = event as CustomEvent<{ href?: string; target?: string }>;
    const href = customEvent.detail?.href;

    if (!href) {
      return;
    }

    window.open(href, customEvent.detail?.target ?? '_blank', 'noopener,noreferrer');
  }

  onFooterAction(item: CarSidebarFooterItem): void {
    if (item.action === 'logout') {
      this.logout();
      return;
    }

    if (item.action === 'support') {
      this.supportDrawerOpen = true;
    }
  }

  closeSupportDrawer(): void {
    this.supportDrawerOpen = false;
    this.expandedFaqIds.clear();
  }

  isFaqExpanded(itemId: string): boolean {
    return this.expandedFaqIds.has(itemId);
  }

  toggleFaqItem(itemId: string): void {
    if (this.expandedFaqIds.has(itemId)) {
      this.expandedFaqIds.delete(itemId);
      return;
    }

    this.expandedFaqIds.add(itemId);
  }

  trackSupportSection(_index: number, section: SupportFaqSection): string {
    return section.id;
  }

  trackSupportFaqItem(_index: number, item: SupportFaqItem): string {
    return item.id;
  }

  isHomeActive(): boolean {
    return this.isRouteActive('pagina-inicial');
  }

  isLinkActive(link?: string): boolean {
    if (!link) {
      return false;
    }

    return this.isRouteActive(link);
  }

  isGroupActive(group: CarSidebarGroupItem): boolean {
    return group.children.some(child => child.type === 'link' && this.isLinkActive(child.link));
  }

  trackChild(_index: number, child: CarSidebarChildItem): string {
    return child.id;
  }

  trackGroup(_index: number, group: CarSidebarGroupItem): string {
    return group.id;
  }

  trackFooter(_index: number, item: CarSidebarFooterItem): string {
    return item.id;
  }

  private validateWindowSize(): void {
    this.large = this.windowSizeService._width < 1024;
    this.subscription.add(
      this.windowSizeService._widthSubs.subscribe(width => {
        this.large = width < 1024;
      }),
    );
  }

  private trackActiveRoute(): void {
    this.activeRoute = this.router.url;
    this.syncExpandedGroups();

    this.subscription.add(
      this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(event => {
        this.activeRoute = (event as NavigationEnd).urlAfterRedirects;
        this.syncExpandedGroups();
      }),
    );
  }

  private syncExpandedGroups(): void {
    if (!this.sidebarNav?.groups.length) {
      return;
    }

    this.sidebarNav.groups.forEach(group => {
      if (this.isGroupActive(group)) {
        group.expanded = true;
      }
    });
  }

  private isRouteActive(link: string): boolean {
    const normalizedLink = link.replace(/^\//, '');
    const current = this.activeRoute.replace(/^\//, '').split('?')[0];

    if (!normalizedLink) {
      return false;
    }

    return current === normalizedLink || current.startsWith(`${normalizedLink}/`);
  }

  private syncLayoutClasses(): void {
    const menu = document.getElementById('car-sidebar');
    const container = document.getElementById('container');

    if (this.collapsed) {
      menu?.classList.add('car-sidebar--collapsed');
      container?.classList.add('container-menu-minimized');
      return;
    }

    menu?.classList.remove('car-sidebar--collapsed');
    container?.classList.remove('container-menu-minimized');
  }

  private logout(): void {
    this.loggedInService.redirectlogout();
  }
}
